/**
 * Comprehensive language configuration for multi-language support
 * 120+ languages covering all countries with supported currencies
 */

export interface LanguageConfig {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  rtl?: boolean;
  enabled?: boolean;
  currencies?: string[]; // Suggested currencies for this language
}

export const LANGUAGES: LanguageConfig[] = [
  // ========== TIER 1: MAJOR GLOBAL LANGUAGES ==========
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', enabled: true, currencies: ['USD', 'GBP', 'EUR', 'AUD', 'CAD', 'NZD', 'SGD', 'HKD'] },
  { code: 'zh', name: 'Chinese (Simplified)', nativeName: '简体中文', flag: '🇨🇳', enabled: true, currencies: ['CNY'] },
  { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '繁體中文', flag: '🇹🇼', enabled: true, currencies: ['TWD', 'HKD', 'MOP'] },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', enabled: true, currencies: ['EUR', 'MXN', 'ARS', 'CLP', 'COP', 'PEN'] },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', rtl: true, enabled: true, currencies: ['SAR', 'AED', 'KWD', 'QAR', 'BHD', 'OMR', 'JOD', 'EGP', 'MAD', 'TND', 'DZD'] },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', enabled: true, currencies: ['INR'] },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', enabled: true, currencies: ['EUR', 'CHF', 'CAD'] },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', enabled: true, currencies: ['RUB', 'KZT', 'BYN'] },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹', enabled: true, currencies: ['EUR', 'BRL'] },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', enabled: true, currencies: ['EUR', 'CHF'] },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', enabled: true, currencies: ['JPY'] },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', enabled: true, currencies: ['KRW'] },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷', enabled: true, currencies: ['TRY'] },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', enabled: true, currencies: ['EUR', 'CHF'] },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩', enabled: true, currencies: ['IDR'] },

  // ========== TIER 2: EUROPEAN LANGUAGES ==========
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱', enabled: true, currencies: ['EUR'] },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱', enabled: true, currencies: ['PLN'] },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦', enabled: true, currencies: ['UAH'] },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴', enabled: true, currencies: ['RON'] },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿', enabled: true, currencies: ['CZK'] },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', flag: '🇭🇺', enabled: true, currencies: ['HUF'] },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪', enabled: true, currencies: ['SEK'] },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk', flag: '🇳🇴', enabled: true, currencies: ['NOK'] },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', flag: '🇩🇰', enabled: true, currencies: ['DKK'] },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', flag: '🇫🇮', enabled: true, currencies: ['EUR'] },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', flag: '🇬🇷', enabled: true, currencies: ['EUR'] },
  { code: 'bg', name: 'Bulgarian', nativeName: 'Български', flag: '🇧🇬', enabled: true, currencies: ['BGN'] },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski', flag: '🇭🇷', enabled: true, currencies: ['EUR'] },
  { code: 'sr', name: 'Serbian', nativeName: 'Српски', flag: '🇷🇸', enabled: true, currencies: ['RSD'] },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', flag: '🇸🇰', enabled: true, currencies: ['EUR'] },
  { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina', flag: '🇸🇮', enabled: true, currencies: ['EUR'] },
  { code: 'et', name: 'Estonian', nativeName: 'Eesti', flag: '🇪🇪', enabled: true, currencies: ['EUR'] },
  { code: 'lv', name: 'Latvian', nativeName: 'Latviešu', flag: '🇱🇻', enabled: true, currencies: ['EUR'] },
  { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių', flag: '🇱🇹', enabled: true, currencies: ['EUR'] },
  { code: 'is', name: 'Icelandic', nativeName: 'Íslenska', flag: '🇮🇸', enabled: true, currencies: ['ISK'] },
  { code: 'ga', name: 'Irish', nativeName: 'Gaeilge', flag: '🇮🇪', enabled: true, currencies: ['EUR'] },
  { code: 'mt', name: 'Maltese', nativeName: 'Malti', flag: '🇲🇹', enabled: true, currencies: ['EUR'] },
  { code: 'cy', name: 'Welsh', nativeName: 'Cymraeg', flag: '🏴󐁧󐁢󐁷󐁬󐁳󐁿', enabled: true, currencies: ['GBP'] },
  { code: 'sq', name: 'Albanian', nativeName: 'Shqip', flag: '🇦🇱', enabled: true, currencies: ['ALL', 'EUR'] },
  { code: 'mk', name: 'Macedonian', nativeName: 'Македонски', flag: '🇲🇰', enabled: true, currencies: ['MKD'] },
  { code: 'bs', name: 'Bosnian', nativeName: 'Bosanski', flag: '🇧🇦', enabled: true, currencies: ['BAM'] },
  { code: 'eu', name: 'Basque', nativeName: 'Euskara', flag: '🇪🇸', enabled: true, currencies: ['EUR'] },
  { code: 'gl', name: 'Galician', nativeName: 'Galego', flag: '🇪🇸', enabled: true, currencies: ['EUR'] },
  { code: 'ca', name: 'Catalan', nativeName: 'Català', flag: '🇦🇩', enabled: true, currencies: ['EUR'] },
  { code: 'lb', name: 'Luxembourgish', nativeName: 'Lëtzebuergesch', flag: '🇱🇺', enabled: true, currencies: ['EUR'] },
  { code: 'be', name: 'Belarusian', nativeName: 'Беларуская', flag: '🇧🇾', enabled: true, currencies: ['BYN'] },
  { code: 'fo', name: 'Faroese', nativeName: 'Føroyskt', flag: '🇫🇴', enabled: true, currencies: ['DKK'] },
  { code: 'fy', name: 'Frisian', nativeName: 'Frysk', flag: '🇳🇱', enabled: true, currencies: ['EUR'] },
  { code: 'gd', name: 'Scottish Gaelic', nativeName: 'Gàidhlig', flag: '🏴󐁧󐁢󐁳󐁣󐁴󐁿', enabled: true, currencies: ['GBP'] },

  // ========== TIER 3: TURKIC & CAUCASUS LANGUAGES ==========
  { code: 'az', name: 'Azerbaijani', nativeName: 'Azərbaycan', flag: '🇦🇿', enabled: true, currencies: ['AZN'] },
  { code: 'kk', name: 'Kazakh', nativeName: 'Қазақша', flag: '🇰🇿', enabled: true, currencies: ['KZT'] },
  { code: 'uz', name: 'Uzbek', nativeName: 'Oʻzbekcha', flag: '🇺🇿', enabled: true, currencies: ['UZS'] },
  { code: 'ky', name: 'Kyrgyz', nativeName: 'Кыргызча', flag: '🇰🇬', enabled: true, currencies: ['KGS'] },
  { code: 'tk', name: 'Turkmen', nativeName: 'Türkmençe', flag: '🇹🇲', enabled: true, currencies: ['TMT'] },
  { code: 'ka', name: 'Georgian', nativeName: 'ქართული', flag: '🇬🇪', enabled: true, currencies: ['GEL'] },
  { code: 'hy', name: 'Armenian', nativeName: 'Հայերեն', flag: '🇦🇲', enabled: true, currencies: ['AMD'] },

  // ========== TIER 4: MIDDLE EAST & PERSIAN ==========
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', flag: '🇮🇱', rtl: true, enabled: true, currencies: ['ILS'] },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', flag: '🇮🇷', rtl: true, enabled: true, currencies: ['IRR'] },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', flag: '🇵🇰', rtl: true, enabled: true, currencies: ['PKR'] },
  { code: 'ps', name: 'Pashto', nativeName: 'پښتو', flag: '🇦🇫', rtl: true, enabled: true, currencies: ['AFN', 'PKR'] },
  { code: 'ku', name: 'Kurdish', nativeName: 'Kurdî', flag: '🇮🇶', enabled: true, currencies: ['IQD', 'IRR', 'TRY'] },
  { code: 'ckb', name: 'Central Kurdish', nativeName: 'کوردیی ناوەندی', flag: '🇮🇶', rtl: true, enabled: true, currencies: ['IQD', 'IRR'] },
  { code: 'sd', name: 'Sindhi', nativeName: 'سنڌي', flag: '🇵🇰', rtl: true, enabled: true, currencies: ['PKR', 'INR'] },
  { code: 'yi', name: 'Yiddish', nativeName: 'ייִדיש', flag: '🇮🇱', rtl: true, enabled: true, currencies: ['ILS', 'USD'] },

  // ========== TIER 5: SOUTH ASIAN LANGUAGES ==========
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', flag: '🇧🇩', enabled: true, currencies: ['BDT', 'INR'] },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳', enabled: true, currencies: ['INR'] },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी', flag: '🇮🇳', enabled: true, currencies: ['INR'] },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳', enabled: true, currencies: ['INR', 'LKR', 'SGD'] },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', flag: '🇮🇳', enabled: true, currencies: ['INR'] },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', flag: '🇮🇳', enabled: true, currencies: ['INR'] },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', flag: '🇮🇳', enabled: true, currencies: ['INR'] },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: '🇮🇳', enabled: true, currencies: ['INR', 'PKR'] },
  { code: 'or', name: 'Odia', nativeName: 'ଓଡ଼ିଆ', flag: '🇮🇳', enabled: true, currencies: ['INR'] },
  { code: 'si', name: 'Sinhala', nativeName: 'සිංහල', flag: '🇱🇰', enabled: true, currencies: ['LKR'] },
  { code: 'ne', name: 'Nepali', nativeName: 'नेपाली', flag: '🇳🇵', enabled: true, currencies: ['NPR'] },
  { code: 'as', name: 'Assamese', nativeName: 'অসমীয়া', flag: '🇮🇳', enabled: true, currencies: ['INR'] },

  // ========== TIER 6: SOUTHEAST ASIAN LANGUAGES ==========
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭', enabled: true, currencies: ['THB'] },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳', enabled: true, currencies: ['VND'] },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', flag: '🇲🇾', enabled: true, currencies: ['MYR', 'SGD', 'BND'] },
  { code: 'fil', name: 'Filipino', nativeName: 'Filipino', flag: '🇵🇭', enabled: true, currencies: ['PHP'] },
  { code: 'my', name: 'Burmese', nativeName: 'မြန်မာ', flag: '🇲🇲', enabled: true, currencies: ['MMK'] },
  { code: 'km', name: 'Khmer', nativeName: 'ខ្មែរ', flag: '🇰🇭', enabled: true, currencies: ['KHR'] },
  { code: 'lo', name: 'Lao', nativeName: 'ລາວ', flag: '🇱🇦', enabled: true, currencies: ['LAK'] },

  // ========== TIER 7: AFRICAN LANGUAGES ==========
  { code: 'af', name: 'Afrikaans', nativeName: 'Afrikaans', flag: '🇿🇦', enabled: true, currencies: ['ZAR'] },
  { code: 'zu', name: 'Zulu', nativeName: 'isiZulu', flag: '🇿🇦', enabled: true, currencies: ['ZAR'] },
  { code: 'xh', name: 'Xhosa', nativeName: 'isiXhosa', flag: '🇿🇦', enabled: true, currencies: ['ZAR'] },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', flag: '🇰🇪', enabled: true, currencies: ['KES', 'TZS'] },
  { code: 'ha', name: 'Hausa', nativeName: 'Hausa', flag: '🇳🇬', enabled: true, currencies: ['NGN'] },
  { code: 'yo', name: 'Yoruba', nativeName: 'Yorùbá', flag: '🇳🇬', enabled: true, currencies: ['NGN'] },
  { code: 'ig', name: 'Igbo', nativeName: 'Igbo', flag: '🇳🇬', enabled: true, currencies: ['NGN'] },
  { code: 'am', name: 'Amharic', nativeName: 'አማርኛ', flag: '🇪🇹', enabled: true, currencies: ['ETB'] },
  { code: 'so', name: 'Somali', nativeName: 'Soomaali', flag: '🇸🇴', enabled: true, currencies: ['SOS'] },
  { code: 'sn', name: 'Shona', nativeName: 'chiShona', flag: '🇿🇼', enabled: true, currencies: ['USD', 'ZAR'] },
  { code: 'st', name: 'Sesotho', nativeName: 'Sesotho', flag: '🇱🇸', enabled: true, currencies: ['LSL', 'ZAR'] },
  { code: 'ny', name: 'Chichewa', nativeName: 'Chichewa', flag: '🇲🇼', enabled: true, currencies: ['MWK'] },
  { code: 'mg', name: 'Malagasy', nativeName: 'Malagasy', flag: '🇲🇬', enabled: true, currencies: ['MGA'] },
  { code: 'rw', name: 'Kinyarwanda', nativeName: 'Kinyarwanda', flag: '🇷🇼', enabled: true, currencies: ['RWF'] },
  { code: 'ln', name: 'Lingala', nativeName: 'Lingála', flag: '🇨🇩', enabled: true, currencies: ['CDF'] },

  // ========== TIER 8: OTHER ASIAN LANGUAGES ==========
  { code: 'mn', name: 'Mongolian', nativeName: 'Монгол', flag: '🇲🇳', enabled: true, currencies: ['MNT'] },
  { code: 'bo', name: 'Tibetan', nativeName: 'བོད་ཡིག', flag: '🏔️', enabled: true, currencies: ['CNY'] },
  { code: 'ug', name: 'Uyghur', nativeName: 'ئۇيغۇرچە', flag: '🇨🇳', rtl: true, enabled: true, currencies: ['CNY'] },
  { code: 'tg', name: 'Tajik', nativeName: 'Тоҷикӣ', flag: '🇹🇯', enabled: true, currencies: ['TJS'] },
  { code: 'tt', name: 'Tatar', nativeName: 'Татарча', flag: '🇷🇺', enabled: true, currencies: ['RUB'] },
  { code: 'ba', name: 'Bashkir', nativeName: 'Башҡортса', flag: '🇷🇺', enabled: true, currencies: ['RUB'] },
  { code: 'tl', name: 'Tagalog', nativeName: 'Tagalog', flag: '🇵🇭', enabled: true, currencies: ['PHP'] },
  { code: 'jv', name: 'Javanese', nativeName: 'Basa Jawa', flag: '🇮🇩', enabled: true, currencies: ['IDR'] },
  { code: 'su', name: 'Sundanese', nativeName: 'Basa Sunda', flag: '🇮🇩', enabled: true, currencies: ['IDR'] },
  { code: 'ceb', name: 'Cebuano', nativeName: 'Sinugbuanon', flag: '🇵🇭', enabled: true, currencies: ['PHP'] },
  { code: 'hmn', name: 'Hmong', nativeName: 'Hmoob', flag: '🇱🇦', enabled: true, currencies: ['LAK', 'THB'] },

  // ========== TIER 9: LATIN AMERICAN VARIANTS ==========
  { code: 'es-MX', name: 'Spanish (Mexico)', nativeName: 'Español (México)', flag: '🇲🇽', enabled: true, currencies: ['MXN'] },
  { code: 'es-AR', name: 'Spanish (Argentina)', nativeName: 'Español (Argentina)', flag: '🇦🇷', enabled: true, currencies: ['ARS'] },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', nativeName: 'Português (Brasil)', flag: '🇧🇷', enabled: true, currencies: ['BRL'] },

  // ========== TIER 10: PACIFIC & OTHERS ==========
  { code: 'mi', name: 'Maori', nativeName: 'Te Reo Māori', flag: '🇳🇿', enabled: true, currencies: ['NZD'] },
  { code: 'sm', name: 'Samoan', nativeName: 'Gagana Samoa', flag: '🇼🇸', enabled: true, currencies: ['WST'] },
  { code: 'to', name: 'Tongan', nativeName: 'Lea Fakatonga', flag: '🇹🇴', enabled: true, currencies: ['TOP'] },
  { code: 'haw', name: 'Hawaiian', nativeName: 'ʻŌlelo Hawaiʻi', flag: '🇺🇸', enabled: true, currencies: ['USD'] },
  
  // ========== TIER 11: RARE & CONSTRUCTED LANGUAGES ==========
  { code: 'eo', name: 'Esperanto', nativeName: 'Esperanto', flag: '🌍', enabled: true, currencies: ['EUR', 'USD'] },
  { code: 'la', name: 'Latin', nativeName: 'Latina', flag: '🇻🇦', enabled: true, currencies: ['EUR'] },
  { code: 'co', name: 'Corsican', nativeName: 'Corsu', flag: '🇫🇷', enabled: true, currencies: ['EUR'] },
  { code: 'ht', name: 'Haitian Creole', nativeName: 'Kreyòl Ayisyen', flag: '🇭🇹', enabled: true, currencies: ['HTG', 'USD'] },
];

/**
 * Get language config by code
 */
export function getLanguage(code: string): LanguageConfig | undefined {
  return LANGUAGES.find(lang => lang.code === code);
}

/**
 * Get enabled languages only
 */
export function getEnabledLanguages(): LanguageConfig[] {
  return LANGUAGES.filter(lang => lang.enabled);
}

/**
 * Get language name in current language
 */
export function getLanguageName(code: string, currentLanguage: string = 'en'): string {
  const lang = getLanguage(code);
  if (!lang) return code;
  
  // Return native name for now, can be extended with translations
  return lang.nativeName;
}

/**
 * Check if language is RTL (Right-to-Left)
 */
export function isRTL(code: string): boolean {
  const lang = getLanguage(code);
  return lang?.rtl || false;
}

/**
 * Get recommended languages for a currency
 */
export function getLanguagesForCurrency(currency: string): LanguageConfig[] {
  return LANGUAGES.filter(lang => lang.currencies?.includes(currency));
}

/**
 * Get default language codes for a region/currency
 */
export function getDefaultLanguagesForCurrency(currency: string): string[] {
  const currencyLanguageMap: Record<string, string[]> = {
    // Major Currencies
    'TRY': ['tr', 'en'],
    'USD': ['en'],
    'EUR': ['de', 'fr', 'it', 'es', 'nl', 'el', 'pt', 'fi', 'en'],
    'GBP': ['en', 'cy'],
    'JPY': ['ja', 'en'],
    'CNY': ['zh', 'en'],
    'KRW': ['ko', 'en'],
    'CHF': ['de', 'fr', 'it', 'en'],
    
    // Middle East
    'SAR': ['ar', 'en'],
    'AED': ['ar', 'en'],
    'KWD': ['ar', 'en'],
    'QAR': ['ar', 'en'],
    'BHD': ['ar', 'en'],
    'OMR': ['ar', 'en'],
    'JOD': ['ar', 'en'],
    'ILS': ['he', 'ar', 'en'],
    'EGP': ['ar', 'en'],
    'IRR': ['fa', 'en'],
    
    // CIS & Turkic
    'RUB': ['ru', 'en'],
    'AZN': ['az', 'ru', 'en'],
    'KZT': ['kk', 'ru', 'en'],
    'UZS': ['uz', 'ru', 'en'],
    'UAH': ['uk', 'ru', 'en'],
    'GEL': ['ka', 'en'],
    'TMT': ['tk', 'ru', 'en'],
    'KGS': ['ky', 'ru', 'en'],
    'AMD': ['hy', 'ru', 'en'],
    
    // South Asia
    'INR': ['hi', 'en', 'bn', 'te', 'mr', 'ta', 'gu', 'kn', 'ml', 'pa', 'or'],
    'PKR': ['ur', 'en', 'pa', 'ps'],
    'BDT': ['bn', 'en'],
    'LKR': ['si', 'ta', 'en'],
    'NPR': ['ne', 'en'],
    'AFN': ['ps', 'fa', 'en'],
    
    // Southeast Asia
    'THB': ['th', 'en'],
    'VND': ['vi', 'en'],
    'IDR': ['id', 'en'],
    'MYR': ['ms', 'en', 'zh'],
    'PHP': ['fil', 'tl', 'en'],
    'SGD': ['en', 'zh', 'ms', 'ta'],
    'MMK': ['my', 'en'],
    'KHR': ['km', 'en'],
    'LAK': ['lo', 'en'],
    
    // East Asia
    'TWD': ['zh', 'en'],
    'HKD': ['zh', 'en'],
    'MOP': ['zh', 'pt', 'en'],
    
    // Americas
    'CAD': ['en', 'fr'],
    'MXN': ['es', 'en'],
    'BRL': ['pt', 'en'],
    'ARS': ['es', 'en'],
    'CLP': ['es', 'en'],
    'COP': ['es', 'en'],
    'PEN': ['es', 'en'],
    
    // Europe
    'PLN': ['pl', 'en'],
    'CZK': ['cs', 'en'],
    'HUF': ['hu', 'en'],
    'RON': ['ro', 'en'],
    'BGN': ['bg', 'en'],
    'HRK': ['hr', 'en'],
    'RSD': ['sr', 'en'],
    'SEK': ['sv', 'en'],
    'NOK': ['no', 'en'],
    'DKK': ['da', 'en'],
    'ISK': ['is', 'en'],
    
    // Africa
    'ZAR': ['af', 'en', 'zu', 'xh'],
    'NGN': ['en', 'ha', 'yo', 'ig'],
    'KES': ['sw', 'en'],
    'MAD': ['ar', 'fr', 'en'],
    'TND': ['ar', 'fr', 'en'],
    'ETB': ['am', 'en'],
    
    // Oceania
    'AUD': ['en'],
    'NZD': ['en', 'mi'],
  };
  
  return currencyLanguageMap[currency] || ['en'];
}

/**
 * Language categories for UI organization
 */
export const LANGUAGE_CATEGORIES = {
  major: ['en', 'zh', 'es', 'ar', 'hi', 'fr', 'ru', 'pt', 'de', 'ja', 'ko', 'tr', 'it', 'id'],
  europe: ['nl', 'pl', 'uk', 'ro', 'cs', 'hu', 'sv', 'no', 'da', 'fi', 'el', 'bg', 'hr', 'sr', 'sk', 'sl', 'et', 'lv', 'lt', 'is', 'ga', 'mt', 'cy', 'sq', 'mk', 'bs', 'eu', 'gl', 'ca', 'lb', 'be', 'fo', 'fy', 'gd'],
  turkicCaucasus: ['az', 'kk', 'uz', 'ky', 'tk', 'ka', 'hy'],
  middleEast: ['he', 'fa', 'ur', 'ps', 'ku', 'ckb', 'sd', 'yi'],
  southAsia: ['bn', 'te', 'mr', 'ta', 'gu', 'kn', 'ml', 'pa', 'or', 'si', 'ne', 'as'],
  southeastAsia: ['th', 'vi', 'ms', 'fil', 'tl', 'my', 'km', 'lo', 'jv', 'su', 'ceb', 'hmn'],
  africa: ['af', 'zu', 'xh', 'sw', 'ha', 'yo', 'ig', 'am', 'so', 'sn', 'st', 'ny', 'mg', 'rw', 'ln'],
  other: ['mn', 'bo', 'ug', 'tg', 'tt', 'ba', 'mi', 'sm', 'to', 'haw', 'eo', 'la', 'co', 'ht'],
};

/**
 * Get language statistics
 */
export function getLanguageStats() {
  return {
    total: LANGUAGES.length,
    enabled: LANGUAGES.filter(l => l.enabled).length,
    rtl: LANGUAGES.filter(l => l.rtl).length,
    withCurrency: LANGUAGES.filter(l => l.currencies && l.currencies.length > 0).length,
  };
}